import { useEffect, useMemo, useRef, useState } from 'react';
import { CampaignDetailsSection } from './sections/CampaignDetailsSection';
import { OrientationPicker } from './sections/OrientationPicker';
import { ScreenPlansList } from './sections/ScreenPlansList';
import { AddMoreScreens } from './sections/AddMoreScreens';
import { UnusedBudgetPanel } from './sections/UnusedBudgetPanel';
import { CreativeAndLaunchSection } from './sections/CreativeAndLaunchSection';
import { BillSummary, type BillSummaryProps } from './BillSummary';
import { MobileBillBar } from './MobileBillBar';
import { useAiPreview } from './useAiPreview';
import { aiScreenKey, maxPlaysPerDay } from './aiPriceEstimate';
import type { AIPreviewProps } from './AIPreview.types';
import { DepositModal } from '@/features/wallet/components/DepositModal';
import { formatMoney } from '@/shared/lib/format/money';
import { CampaignBusyOverlay } from '@/features/campaigns/components/CampaignBusyOverlay';
import { OffersCard } from './sections/OffersCard';

/** The smallest top-up allowed, and the amounts offered as one-tap choices. */
const MIN_TOP_UP = 100;
const TOP_UP_PRESETS = [100, 200, 500, 800, 1000];

export function AIPreview(props: AIPreviewProps) {
  const preview = useAiPreview(props);
  // Never below the minimum, and always enough to cover what the campaign is short by.
  const topUpAmount = Math.max(MIN_TOP_UP, preview.depositAmount);
  const { customisation, estimate } = preview;

  const replanningOrientation = preview.isCouponRefreshing && preview.regenReason === 'orientation';
  const busy = replanningOrientation || preview.isRepricing;

  const launchDisabled =
    !preview.canLaunch || preview.isLaunching || preview.isCouponRefreshing || preview.isRepricing;

  // The hours each screen plays in, so every card can say when the ad runs.
  const hoursById = useMemo(() => {
    const hours: Record<string, Set<number>> = {};
    for (const slot of preview.slots) {
      if (!Array.isArray(slot.billboards)) continue;
      for (const entry of slot.billboards as Array<string | { billboardId?: string; _id?: string }>) {
        const id = typeof entry === 'string' ? entry : String(entry.billboardId || entry._id || '');
        if (!id) continue;
        (hours[id] ??= new Set()).add(Number(slot.hour));
      }
    }
    return Object.fromEntries(
      Object.entries(hours).map(([id, set]) => [id, [...set].sort((a, b) => a - b)]),
    );
  }, [preview.slots]);

  const maxPlaysByKey = useMemo(
    () =>
      Object.fromEntries(
        preview.screens.map((screen) => {
          const key = aiScreenKey(screen);
          return [
            key,
            maxPlaysPerDay({
              screen,
              currentPlays: customisation.playsByKey[key] ?? 0,
              currentTotal: estimate.total,
              days: preview.days,
              // No budget cap: going over budget is allowed once the user agrees to pay the difference.
              budget: null,
              capacityCap: preview.capacityTotals[String(screen._id)],
            }),
          ];
        }),
      ),
    [preview.screens, customisation.playsByKey, estimate.total, preview.days, preview.capacityTotals],
  );

  // After the user edits, GST rows scale with the live estimate; otherwise the server's rows show.
  const planTax = preview.taxRows.reduce((sum, row) => sum + row.value, 0);
  const taxRows = !preview.needsSave
    ? preview.taxRows
    : planTax > 0
      ? preview.taxRows.map((row) => ({ ...row, value: (row.value / planTax) * estimate.tax }))
      : [{ label: 'GST', value: estimate.tax }];

  // The full bill sits at the end of the page. On phones a bar stays pinned to the bottom until the
  // user scrolls to the full bill, then steps aside so the bill can be read and paid directly.
  const inlineBillRef = useRef<HTMLElement>(null);
  const [inlineBillVisible, setInlineBillVisible] = useState(false);
  useEffect(() => {
    const element = inlineBillRef.current;
    if (!element || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver(([entry]) => setInlineBillVisible(entry.isIntersecting), {
      // Count it as visible a little before it reaches the very bottom, where the bar would sit.
      rootMargin: '0px 0px -72px 0px',
      threshold: 0.2,
    });
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const billProps: BillSummaryProps = {
    total: preview.cost,
    subtotal: preview.needsSave ? estimate.subtotal : (preview.amountWithoutTax ?? estimate.subtotal),
    taxRows,
    discount: preview.needsSave ? estimate.discount : preview.appliedDiscount,
    budget: preview.planBudget,
    overBudget: preview.overBudget,
    availableBalance: preview.availableBalance,
    balanceOk: preview.balanceOk,
    showLaunch: true,
    launchDisabled: launchDisabled,
    isLaunching: preview.isLaunching,
    needsTopUp: preview.needsTopUp,
    blockedReason: preview.launchBlockedReason,
    updating: busy,
    estimated: preview.needsSave,
    warning: preview.creativeWarning,
    payLabel: preview.creativeWarning ? 'Pay anyway' : 'Pay & Launch',
    overBudgetAmount: preview.overBudgetAmount,
    overBudgetAccepted: preview.overBudgetAccepted,
    onOverBudgetAcceptedChange: preview.setOverBudgetAccepted,
    walletCoversPayable: preview.balanceOk,
    walletShortfall: preview.launchShortfall,
    saving: preview.isRepricing,
    onLaunch: preview.onLaunch,
  };

  return (
    <div className="camp-fade-up flex w-full flex-col gap-4 pb-24 lg:pb-0">
      <CampaignBusyOverlay
        open={preview.isLaunching}
        title="Launching campaign…"
        message="Confirming payment and creating your campaign. Please don’t close this page."
      />
      <header className="text-left">
        <p className="m-0 text-[11px] font-semibold tracking-[0.12em] text-[var(--dash-success)] uppercase">
          Plan ready
        </p>
        <p className="mt-2 mb-0 text-sm text-[var(--dash-muted)]">
          Everything is pre-filled by AI. Add your creative and pay to go live, or change anything first.
        </p>
      </header>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[minmax(0,1.5fr)_minmax(0,0.9fr)] lg:items-start">
        <div className="flex min-w-0 flex-col gap-4">
          <OrientationPicker
            values={customisation.orientations}
            recommended={preview.aiOrientation}
            loading={replanningOrientation}
            onChange={preview.onOrientationChange}
          />

          <ScreenPlansList
            screens={preview.screens}
            selectedKeys={customisation.selectedKeys}
            playsByKey={customisation.playsByKey}
            maxPlaysByKey={maxPlaysByKey}
            hoursById={hoursById}
            days={preview.days}
            busy={busy}
            isCustomised={customisation.isCustomised && !replanningOrientation}
            onToggle={customisation.toggleScreen}
            onPlaysChange={customisation.setPlays}
            onReset={customisation.reset}
            remainingBudget={preview.unusedBudget}
            canFillBudget={preview.canFillBudget}
            onFillBudget={preview.fillRemainingBudget}
            onEqualPlays={preview.equalizePlays}
          />

          {preview.hasUnusedBudget ? (
            <UnusedBudgetPanel
              remaining={preview.unusedBudget ?? 0}
              budget={preview.planBudget ?? 0}
              canFill={preview.canFillBudget}
              recommendation={preview.fillPlan}
              onFill={preview.fillRemainingBudget}
              suggestions={preview.budgetSuggestions}
              addingId={preview.addingScreenId}
              disabled={busy}
              onAdd={preview.addScreen}
              accepted={preview.unusedAccepted}
              onAcceptedChange={preview.setUnusedAccepted}
              spending={preview.cost}
            />
          ) : null}

          <AddMoreScreens
            screens={preview.addableScreens}
            loading={preview.addableLoading}
            playPrice={preview.estimatePlayPrice}
            days={preview.days}
            remainingBudget={preview.remainingBudget}
            addingId={preview.addingScreenId}
            disabled={busy}
            orientationLabel={customisation.orientations.map((o) => (o === 'PORTRAIT' ? 'Portrait' : 'Landscape')).join(' and ')}
            onAdd={preview.addScreen}
          />

          <CreativeAndLaunchSection
            required={preview.required}
            uploadOrientations={preview.uploadOrientations}
            missingRequired={preview.missingRequired}
            uploads={preview.uploads}
            previewByOrientation={preview.previewByOrientation}
            onUploaded={preview.onUploaded}
            onCleared={preview.onCleared}
            onPreviewChange={preview.onPreviewChange}
            dummyForm={preview.dummyForm}
            onUploadingChange={preview.setIsUploading}
          />

          <CampaignDetailsSection
            campaignName={preview.campaignName}
            onCampaignNameChange={preview.onCampaignNameChange}
            dateRange={preview.dateRange}
            totalRepetitions={preview.totalRepetitions}
            durationSec={preview.durationSec}
            totalScreens={customisation.selectedKeys.size}
          />

        </div>

        {/* Offers sit right above the bill, so a saving is one tap away when the user is about to pay. */}
        <div className="flex min-w-0 flex-col gap-4 lg:sticky lg:top-24 lg:max-h-[calc(100vh-7rem)] lg:overflow-y-auto">
          <OffersCard
            offers={preview.offers}
            loading={preview.offersLoading}
            appliedCode={preview.appliedCouponCode}
            saving={preview.couponSaving}
            onApply={preview.applyOffer}
            onRemove={preview.removeOffer}
            onApplyTyped={preview.applyTypedCode}
          />
          <BillSummary innerRef={inlineBillRef} {...billProps} />
        </div>
      </div>

      <MobileBillBar hidden={inlineBillVisible} {...billProps} />

      <DepositModal
        open={preview.depositOpen}
        onClose={preview.closeDeposit}
        availableBalance={preview.availableBalance}
        initialAmount={topUpAmount}
        minAmount={topUpAmount}
        quickAmounts={TOP_UP_PRESETS}
        title="Add funds to launch"
        subtitle={`Need ${formatMoney(preview.launchShortfall)} more. ${
          preview.launchShortfall < MIN_TOP_UP
            ? `The minimum top-up is ${formatMoney(MIN_TOP_UP)}; what is left over stays in your wallet.`
            : 'Choose an amount that covers it.'
        } Pay with Razorpay, then we launch automatically.`}
        confirmLabel="Pay with Razorpay"
        onSuccess={preview.handleDepositSuccess}
      />
    </div>
  );
}
